'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import {
  eliminarMarcha,
  formatearBytes,
  formatearDuracion,
  getMarchas,
  getMarchasAlmacenamiento,
  getMarchasManifestState,
  regenerarManifiestoMarchas,
  revisarMarcha,
} from '@/lib/actions';
import type { MarchaDoc, MarchasManifestState } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Check,
  CheckCheck,
  Database,
  HardDrive,
  Loader2,
  Music,
  RefreshCw,
  Trash2,
  X,
  Youtube,
} from 'lucide-react';
import { cn } from '@/lib/utils';

type Pestana = 'pendientes' | 'todas' | 'almacenamiento';

/** Cuánto se espera tras la ÚLTIMA decisión antes de refrescar de fondo. */
const REFRESCO_MS = 4000;

/**
 * Panel de admin de JartiMarchas: aprobar solicitudes, ver el almacenamiento y
 * quitar marchas de cualquier banda.
 *
 * El manifiesto que consume el mod lo republica sola la Cloud Function en cada
 * escritura; el botón «Regenerar» está por si un trigger se pierde.
 */
export default function AdminJartiMarchasPage() {
  const { userData } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [pestana, setPestana] = useState<Pestana>('pendientes');
  const [marchas, setMarchas] = useState<MarchaDoc[]>([]);
  const [cargando, setCargando] = useState(true);
  const [manifiesto, setManifiesto] = useState<MarchasManifestState | null>(null);
  const [uso, setUso] = useState<Awaited<ReturnType<typeof getMarchasAlmacenamiento>>>(null);
  const [busca, setBusca] = useState('');
  const [aRechazar, setARechazar] = useState<MarchaDoc | null>(null);
  const [motivo, setMotivo] = useState('');
  const [aBorrar, setABorrar] = useState<MarchaDoc | null>(null);
  const [regenerando, setRegenerando] = useState(false);
  /** Ids con una decisión en vuelo: solo bloquean SU fila, no la página. */
  const [ocupadas, setOcupadas] = useState<Record<string, boolean>>({});
  /** Progreso de la aprobación en tanda (0 = no hay tanda en marcha). */
  const [tandaTotal, setTandaTotal] = useState(0);
  const [tandaHechas, setTandaHechas] = useState(0);
  const [confirmarTanda, setConfirmarTanda] = useState(false);
  const refrescoRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (userData && userData.role !== 'admin') navigate('/jartinet/home');
  }, [userData, navigate]);

  const recargar = useCallback(async (silencioso = false) => {
    if (!silencioso) setCargando(true);
    const [ms, mf, u] = await Promise.all([
      getMarchas(),
      getMarchasManifestState(),
      getMarchasAlmacenamiento(),
    ]);
    setMarchas(ms);
    setManifiesto(mf);
    setUso(u);
    if (!silencioso) setCargando(false);
  }, []);

  useEffect(() => { recargar(); }, [recargar]);
  useEffect(() => () => { if (refrescoRef.current) clearTimeout(refrescoRef.current); }, []);

  /**
   * Refresco de fondo AGRUPADO tras una tanda de decisiones: sin spinner y solo
   * cuando se para de pulsar. Aprobar veinte seguidas no puede recargar la página
   * veinte veces — la lista ya se actualiza sola, esto solo cuadra los totales.
   */
  const refrescarLuego = useCallback(() => {
    if (refrescoRef.current) clearTimeout(refrescoRef.current);
    refrescoRef.current = setTimeout(() => { recargar(true); }, REFRESCO_MS);
  }, [recargar]);

  /** Aplica el cambio en memoria (`null` = quitar la marcha de la lista). */
  const aplicarEnLista = useCallback((id: string, cambio: Partial<MarchaDoc> | null) => {
    setMarchas((prev) => (cambio === null
      ? prev.filter((x) => x.id !== id)
      : prev.map((x) => (x.id === id ? { ...x, ...cambio } : x))));
  }, []);

  /** Mueve una marcha de un contador a otro (`null` = se borró). */
  const ajustarUso = useCallback((antes: MarchaDoc['estado'], despues: MarchaDoc['estado'] | null) => {
    setUso((u) => {
      if (!u) return u;
      const n = { ...u };
      n[antes] = Math.max(0, (n[antes] ?? 0) - 1);
      if (despues) n[despues] = (n[despues] ?? 0) + 1;
      return n;
    });
  }, []);

  const pendientes = useMemo(() => marchas.filter((m) => m.estado === 'pendiente'), [marchas]);
  const enTanda = tandaTotal > 0;

  const filtradas = useMemo(() => {
    const base = pestana === 'pendientes' ? pendientes : marchas;
    const q = busca.trim().toLowerCase();
    if (!q) return base;
    return base.filter((m) =>
      [m.nombre, m.bandaNombre, m.repertorioItem, m.solicitadaPorNombre]
        .some((s) => (s || '').toLowerCase().includes(q)));
  }, [pestana, pendientes, marchas, busca]);

  /** Cuántas de las que se ven ahora mismo están pendientes. */
  const pendientesVisibles = useMemo(
    () => filtradas.filter((m) => m.estado === 'pendiente').length,
    [filtradas],
  );

  /**
   * Aprueba de una tacada todas las pendientes que se estén viendo (respeta el
   * buscador: lo que hay en pantalla es lo que se aprueba). Va de una en una a
   * propósito —cada aprobación republica el manifiesto— pero sin recargar entre
   * medias: la lista se va vaciando sola y al final sale UN solo aviso.
   */
  const aprobarTodas = async () => {
    const lote = filtradas.filter((m) => m.estado === 'pendiente');
    if (lote.length === 0) return;
    setTandaTotal(lote.length);
    setTandaHechas(0);

    let ok = 0;
    const fallidas: string[] = [];
    for (const m of lote) {
      const res = await revisarMarcha(m.id, 'aceptada');
      if (res.success) {
        ok++;
        aplicarEnLista(m.id, { estado: 'aceptada' });
        ajustarUso(m.estado, 'aceptada');
      } else {
        fallidas.push(m.nombre);
      }
      setTandaHechas((n) => n + 1);
    }

    setTandaTotal(0);
    setTandaHechas(0);
    if (ok > 0) refrescarLuego();
    toast({
      title: fallidas.length === 0 ? 'Listo' : 'Terminado con fallos',
      description: fallidas.length === 0
        ? `${ok} ${ok === 1 ? 'marcha aprobada' : 'marchas aprobadas'}. Ya suenan en el juego.`
        : `${ok} aprobadas · ${fallidas.length} sin aprobar: ${fallidas.slice(0, 3).join(', ')}${fallidas.length > 3 ? '…' : ''}`,
      variant: fallidas.length === 0 ? undefined : 'destructive',
    });
  };

  const marcarOcupada = (id: string, v: boolean) =>
    setOcupadas((p) => {
      if (v) return { ...p, [id]: true };
      const n = { ...p };
      delete n[id];
      return n;
    });

  const aprobar = async (m: MarchaDoc) => {
    if (ocupadas[m.id]) return;
    marcarOcupada(m.id, true);
    const res = await revisarMarcha(m.id, 'aceptada');
    marcarOcupada(m.id, false);
    if (res.success) {
      // La lista se corrige en memoria: nada de recargar y perder el scroll.
      aplicarEnLista(m.id, { estado: 'aceptada' });
      ajustarUso(m.estado, 'aceptada');
      refrescarLuego();
    }
    toast({
      title: res.success ? 'Aprobada' : 'Error',
      description: res.success ? `«${m.nombre}» ya suena en el juego.` : res.message,
      variant: res.success ? undefined : 'destructive',
    });
  };

  const rechazar = async () => {
    if (!aRechazar) return;
    const m = aRechazar;
    setARechazar(null);
    setMotivo('');
    marcarOcupada(m.id, true);
    const res = await revisarMarcha(m.id, 'rechazada', motivo.trim());
    marcarOcupada(m.id, false);
    if (res.success) {
      aplicarEnLista(m.id, { estado: 'rechazada', motivoRechazo: motivo.trim() });
      ajustarUso(m.estado, 'rechazada');
      refrescarLuego();
    }
    toast({
      title: res.success ? 'Rechazada' : 'Error',
      description: res.message,
      variant: res.success ? undefined : 'destructive',
    });
  };

  const borrar = async () => {
    if (!aBorrar) return;
    const m = aBorrar;
    setABorrar(null);
    marcarOcupada(m.id, true);
    const res = await eliminarMarcha(m.id);
    marcarOcupada(m.id, false);
    if (res.success) {
      aplicarEnLista(m.id, null);
      ajustarUso(m.estado, null);
      refrescarLuego();
    }
    toast({
      title: res.success ? 'Eliminada' : 'Error',
      description: res.message,
      variant: res.success ? undefined : 'destructive',
    });
  };

  const regenerar = async () => {
    setRegenerando(true);
    const res = await regenerarManifiestoMarchas();
    setRegenerando(false);
    toast({
      title: res.success ? 'Manifiesto publicado' : 'Error',
      description: res.message,
      variant: res.success ? undefined : 'destructive',
    });
    recargar(true);
  };

  return (
    <div className="space-y-6">
      {/* ── Hero ── */}
      <div className="relative overflow-hidden rounded-2xl border border-white/8 bg-gradient-to-br from-indigo-500/15 via-card/40 to-transparent p-6">
        <div className="absolute -top-16 -right-10 h-48 w-48 rounded-full bg-indigo-500/10 blur-3xl" />
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="rounded-xl border border-indigo-400/30 bg-indigo-500/10 p-2">
              <Music className="h-6 w-6 text-indigo-300" />
            </div>
            <div>
              <h1 className="font-headline text-2xl font-bold">JartiMarchas</h1>
              <p className="text-sm text-muted-foreground">
                Solicitudes de las bandas y estado del catálogo que consume el mod.
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={regenerar} disabled={regenerando}>
            {regenerando
              ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              : <RefreshCw className="mr-2 h-4 w-4" />}
            Regenerar manifiesto
          </Button>
        </div>

        {/* Tiles */}
        <div className="relative mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Tile
            label="Pendientes"
            valor={String(uso?.pendiente ?? pendientes.length)}
            destacado={(uso?.pendiente ?? pendientes.length) > 0}
          />
          <Tile label="En el juego" valor={String(uso?.aceptada ?? 0)} />
          <Tile label="Bandas con audio" valor={String(manifiesto?.bandas ?? 0)} />
          <Tile
            label="Almacenamiento"
            valor={formatearBytes(uso?.bytesStorage ?? 0)}
            sub={`${uso?.ficheros ?? 0} ficheros`}
          />
        </div>

        {manifiesto && (
          <p className="relative mt-3 text-[0.7rem] text-muted-foreground">
            Manifiesto publicado {new Date(manifiesto.generatedAt).toLocaleString('es-ES')} ·
            {' '}{manifiesto.marchas} marchas · hash <code>{manifiesto.hash}</code> ·
            {' '}el servidor del mod lo recoge en 15 s y lo empuja a los jugadores conectados.
          </p>
        )}
      </div>

      {/* ── Pestañas ── */}
      <div className="sticky top-0 z-20 -mx-4 border-b border-white/5 bg-background/85 px-4 py-2 backdrop-blur md:-mx-6 md:px-6">
        <div className="flex flex-wrap items-center gap-2">
          <Tab activa={pestana === 'pendientes'} onClick={() => setPestana('pendientes')}>
            Pendientes {pendientes.length > 0 && (
              <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-bold text-black">
                {pendientes.length}
              </span>
            )}
          </Tab>
          <Tab activa={pestana === 'todas'} onClick={() => setPestana('todas')}>Todas</Tab>
          <Tab activa={pestana === 'almacenamiento'} onClick={() => setPestana('almacenamiento')}>
            Almacenamiento
          </Tab>

          {/* Aprobar de golpe lo que se esté viendo (respeta el buscador). */}
          {pestana !== 'almacenamiento' && pendientesVisibles > 0 && (
            <Button
              size="sm"
              className="bg-emerald-600 text-white hover:bg-emerald-500"
              disabled={enTanda}
              onClick={() => setConfirmarTanda(true)}
            >
              {enTanda ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  Aprobando {tandaHechas} de {tandaTotal}…
                </>
              ) : (
                <>
                  <CheckCheck className="mr-1.5 h-4 w-4" />
                  Aprobar {busca.trim() ? 'las' : 'todas'} ({pendientesVisibles})
                </>
              )}
            </Button>
          )}

          <div className="ml-auto w-full sm:w-64">
            <Input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar marcha, banda…"
              className="h-9"
            />
          </div>
        </div>
      </div>

      {cargando ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : pestana === 'almacenamiento' ? (
        <Almacenamiento uso={uso} marchas={marchas} />
      ) : filtradas.length === 0 ? (
        <div className="rounded-2xl border border-white/8 bg-card/40 p-12 text-center">
          <Music className="mx-auto mb-3 h-10 w-10 text-muted-foreground/30" />
          <p className="text-muted-foreground">
            {pestana === 'pendientes' ? 'No hay solicitudes pendientes.' : 'No hay marchas.'}
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          <AnimatePresence initial={false}>
          {filtradas.map((m) => (
            <motion.li
              key={m.id}
              layout
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, height: 0, marginTop: 0, transition: { duration: 0.18 } }}
              className="relative overflow-hidden rounded-2xl border border-white/8 bg-card/40 p-4"
            >
              {/* Carril de color por estado, como en la tienda */}
              <span className={cn(
                'absolute inset-y-0 left-0 w-1',
                m.estado === 'aceptada' ? 'bg-emerald-500'
                  : m.estado === 'pendiente' ? 'bg-amber-500' : 'bg-red-500',
              )} />
              <div className="flex flex-wrap items-start gap-4 pl-2">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold">{m.nombre}</h3>
                    {m.origen === 'youtube' && (
                      <span className="inline-flex items-center gap-1 rounded-md bg-red-500/15 px-1.5 py-0.5 text-[0.65rem] text-red-300">
                        <Youtube className="h-3 w-3" /> YouTube
                      </span>
                    )}
                    <span className={cn(
                      'rounded-md px-1.5 py-0.5 text-[0.65rem] font-medium',
                      m.estado === 'aceptada' ? 'bg-emerald-500/15 text-emerald-300'
                        : m.estado === 'pendiente' ? 'bg-amber-500/15 text-amber-300'
                          : 'bg-red-500/15 text-red-300',
                    )}>
                      {m.estado}
                    </span>
                  </div>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    <span className="text-foreground/80">{m.bandaNombre}</span>
                    {' · '}repertorio: «{m.repertorioItem}»
                  </p>
                  <p className="mt-0.5 text-[0.7rem] text-muted-foreground">
                    {formatearDuracion(m.duracionMs)} · {formatearBytes(m.bytes)}
                    {m.solicitadaPorNombre && ` · pedida por ${m.solicitadaPorNombre}`}
                    {m.estado === 'rechazada' && m.motivoRechazo && ` · motivo: ${m.motivoRechazo}`}
                  </p>
                </div>

                <audio src={m.audioUrl} controls preload="none" className="h-9 w-full sm:w-64" />

                <div className="flex shrink-0 items-center gap-2">
                  {m.estado !== 'aceptada' && (
                    <Button size="sm" onClick={() => aprobar(m)} disabled={!!ocupadas[m.id] || enTanda}>
                      {ocupadas[m.id]
                        ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                        : <Check className="mr-1.5 h-4 w-4" />}
                      Aprobar
                    </Button>
                  )}
                  {m.estado !== 'rechazada' && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!!ocupadas[m.id] || enTanda}
                      onClick={() => { setARechazar(m); setMotivo(''); }}
                    >
                      <X className="mr-1.5 h-4 w-4" /> Rechazar
                    </Button>
                  )}
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-9 w-9 text-muted-foreground hover:text-destructive"
                    disabled={!!ocupadas[m.id] || enTanda}
                    onClick={() => setABorrar(m)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </motion.li>
          ))}
          </AnimatePresence>
        </ul>
      )}

      {/* Rechazar con motivo */}
      <Dialog open={!!aRechazar} onOpenChange={(o) => !o && setARechazar(null)}>
        <DialogContent className="max-h-[90dvh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle>Rechazar «{aRechazar?.nombre}»</DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
            <Label htmlFor="motivo" className="text-xs">Motivo (lo verá el director)</Label>
            <Input
              id="motivo"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="La grabación se corta al final"
              className="mt-2"
            />
          </div>
          <DialogFooter className="px-6 pb-6">
            <Button variant="ghost" onClick={() => setARechazar(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={rechazar}>Rechazar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Aprobar en tanda */}
      <AlertDialog open={confirmarTanda} onOpenChange={setConfirmarTanda}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              ¿Aprobar {pendientesVisibles} {pendientesVisibles === 1 ? 'marcha' : 'marchas'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {busca.trim()
                ? <>Solo se aprueban las que casan con «{busca.trim()}». </>
                : <>Se aprueban todas las pendientes. </>}
              Pasan a sonar en el juego en cuanto el servidor recoja el manifiesto (unos 15 s).
              Se pueden rechazar después una a una.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={aprobarTodas}>
              Aprobar {pendientesVisibles}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!aBorrar} onOpenChange={(o) => !o && setABorrar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar «{aBorrar?.nombre}»?</AlertDialogTitle>
            <AlertDialogDescription>
              Desaparece del repertorio en el juego. El fichero de audio se borra en la
              siguiente limpieza (tiene unas horas de gracia por si se vuelve a solicitar).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={borrar}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Tile({ label, valor, sub, destacado }: {
  label: string; valor: string; sub?: string; destacado?: boolean;
}) {
  return (
    <div className={cn(
      'rounded-xl border p-3',
      destacado ? 'border-amber-500/40 bg-amber-500/10' : 'border-white/8 bg-card/40',
    )}>
      <p className="text-[0.62rem] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="mt-1 text-xl font-bold">{valor}</p>
      {sub && <p className="text-[0.7rem] text-muted-foreground">{sub}</p>}
    </div>
  );
}

function Tab({ activa, onClick, children }: {
  activa: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center rounded-xl px-3 py-1.5 text-sm transition-colors',
        activa ? 'bg-primary/15 font-semibold text-primary' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function Almacenamiento({ uso, marchas }: {
  uso: Awaited<ReturnType<typeof getMarchasAlmacenamiento>>;
  marchas: MarchaDoc[];
}) {
  const porBanda = useMemo(() => {
    const m = new Map<string, { nombre: string; n: number; bytes: number }>();
    for (const x of marchas) {
      if (x.estado === 'rechazada') continue;
      const e = m.get(x.bandaId) || { nombre: x.bandaNombre || x.bandaId, n: 0, bytes: 0 };
      e.n++;
      e.bytes += x.bytes || 0;
      m.set(x.bandaId, e);
    }
    return [...m.values()].sort((a, b) => b.bytes - a.bytes);
  }, [marchas]);

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-2xl border border-white/8 bg-card/40 p-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <HardDrive className="h-4 w-4" />
            <span className="text-xs uppercase tracking-widest">En Storage</span>
          </div>
          <p className="mt-2 text-2xl font-bold">{formatearBytes(uso?.bytesStorage ?? 0)}</p>
          <p className="text-xs text-muted-foreground">{uso?.ficheros ?? 0} ficheros de audio</p>
        </div>
        <div className="rounded-2xl border border-white/8 bg-card/40 p-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Database className="h-4 w-4" />
            <span className="text-xs uppercase tracking-widest">Referenciado</span>
          </div>
          <p className="mt-2 text-2xl font-bold">{formatearBytes(uso?.bytesUsados ?? 0)}</p>
          <p className="text-xs text-muted-foreground">
            {(uso?.aceptada ?? 0) + (uso?.pendiente ?? 0)} marchas vivas
          </p>
        </div>
        <div className="rounded-2xl border border-white/8 bg-card/40 p-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Music className="h-4 w-4" />
            <span className="text-xs uppercase tracking-widest">Descarga</span>
          </div>
          <p className="mt-2 text-2xl font-bold">
            ~{formatearBytes((uso?.bytesUsados ?? 0) * 30)}
          </p>
          <p className="text-xs text-muted-foreground">
            si 30 jugadores se lo bajan todo (una vez cada uno)
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-white/8 bg-card/40 p-5">
        <h3 className="mb-3 font-headline font-bold">Por banda</h3>
        {porBanda.length === 0 ? (
          <p className="text-sm text-muted-foreground">Todavía no hay marchas.</p>
        ) : (
          <ul className="space-y-1.5">
            {porBanda.map((b) => (
              <li key={b.nombre} className="flex items-center gap-3 rounded-xl bg-muted/40 px-3 py-2">
                <span className="min-w-0 flex-1 truncate text-sm">{b.nombre}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{b.n} marchas</span>
                <span className="w-20 shrink-0 text-right text-xs font-medium">
                  {formatearBytes(b.bytes)}
                </span>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-4 text-[0.7rem] leading-relaxed text-muted-foreground">
          Cada jugador se descarga cada marcha <strong>una sola vez</strong>: el fichero va
          direccionado por el hash de su contenido y se queda en su disco
          (<code>config/modcofrade/marchas/</code>). El servidor del juego no paga ancho de
          banda por esto — el audio va directo de la CDN al jugador.
        </p>
      </div>
    </div>
  );
}
